import { Fragment, useState, type MouseEvent } from "react";

import { Button } from "../../../components/Button/Button";
import { Input } from "../../../components/Input/Input";
import { RangeField } from "../../../components/RangeField/RangeField";
import type { SchemaField } from "../../../components/SchemaEditor/SchemaEditor";
import { SegmentedToggle } from "../../../components/SegmentedToggle/SegmentedToggle";
import { Select } from "../../../components/Select/Select";
import { FieldRow } from "../../projects/config/FieldRow";
import { SectionCard } from "../../projects/config/SectionCard";
import { defaultGrowthRule } from "../config/defaults";
import type {
  BotGrowthMode,
  BotGrowthRule,
  BotPlayRhythm,
  BotProfile,
  BotProfileStatRule,
  BotsLeaderboardSim,
} from "../config/types";
import {
  EVERY_BOUNDS,
  PLAY_BOUNDS,
  PLAY_FIELD_LABELS,
  formatOnlineWeek,
  onlineMinutesPerWeek,
  type BotPlayField,
  type BotsErrors,
} from "./botsValidation";
import {
  capLabel,
  formatGain,
  gainPerPeriod,
  gainPerWeek,
  startMean,
  weeksToCap,
  type ForecastPeriod,
} from "./forecast";
import styles from "./BotsSettings.module.css";
import { scoreModeCell, type BoardShape } from "./scoreMode";

interface Props {
  profiles: BotProfile[];
  onProfiles: (next: BotProfile[]) => void;
  growth: BotGrowthRule[];
  onGrowth: (next: BotGrowthRule[]) => void;
  /** `profile.stats_schema` — its int keys are what can grow. */
  statsSchema: SchemaField[];
  /** `bots.profile.stats` — where a fresh bot's stat starts, for the cap. */
  statRules: BotProfileStatRule[];
  /** `bots.leaderboards[]` — the boards that follow each stat. */
  leaderboards: BotsLeaderboardSim[];
  /** The project's boards, whose operator decides whether one can follow. */
  boards: BoardShape[];
  dirty: boolean;
  errors: BotsErrors;
}

/** The three ranges of a rhythm, in column order, with the unit each is in. */
const RANGES: { field: BotPlayField; unit: string }[] = [
  { field: "days_per_week", unit: "days" },
  { field: "sessions_per_day", unit: "times" },
  { field: "session_minutes", unit: "min" },
];

/** A row that has no rhythm yet edits from six empty boxes, not from numbers
 *  the operator never saw. */
const NO_RHYTHM: BotPlayRhythm = {
  days_per_week_min: NaN,
  days_per_week_max: NaN,
  sessions_per_day_min: NaN,
  sessions_per_day_max: NaN,
  session_minutes_min: NaN,
  session_minutes_max: NaN,
};

const MODE_OPTIONS: { value: BotGrowthMode; label: string }[] = [
  { value: "during_play", label: "During play" },
  { value: "per_day", label: "Per day" },
];

const PERIOD_OPTIONS: { value: ForecastPeriod; label: string }[] = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

/** Scroll to another card of the tab without touching the address bar. */
function scrollTo(id: string) {
  return (e: MouseEvent) => {
    e.preventDefault();
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
}

/**
 * `bots.profiles[].play` and `bots.growth[]` — how long a bot of each profile
 * is online, what grows on every bot, and what that adds up to.
 *
 * Mockup: t-180/bots-settings-progression.html §A and §C. The profiles are
 * Behaviour's, read-only here: a profile is added, renamed and weighted
 * there, and this card only gives each one a rhythm.
 */
export function ProgressionSection({
  profiles,
  onProfiles,
  growth,
  onGrowth,
  statsSchema,
  statRules,
  leaderboards,
  boards,
  dirty,
  errors,
}: Props) {
  const setPlay = (i: number, play: BotPlayRhythm) =>
    onProfiles(profiles.map((p, j) => (i === j ? { ...p, play } : p)));
  const firstError = profiles
    .map((_, i) => errors.playRows[i]?.message)
    .find((m) => m !== undefined);

  return (
    <SectionCard
      id="progression"
      title="Progression"
      apiName="bots.profiles[].play · bots.growth[]"
      dirty={dirty}
    >
      <FieldRow
        name="Play rhythm"
        apiName="bots.profiles[].play"
        hint="How much a bot of each profile plays. On a play day its sessions fall where the Time of day curve puts them; between play days it is offline. A bot grows and submits scores only while it is online, whether or not its guild is awake."
        stacked
      >
        <div className={styles.gridEditor}>
          <div className={styles.scroller}>
            <div className={`${styles.row} ${styles.rhythm} ${styles.head}`}>
              <div>profile</div>
              <div className={styles.num}>share</div>
              {RANGES.map(({ field }) => (
                <div key={field} className={styles.ctr}>
                  {PLAY_FIELD_LABELS[field]}
                </div>
              ))}
              <div className={styles.num}>online / week</div>
            </div>
            {profiles.map((p, i) => {
              const who = p.key.trim() || `profile ${i + 1}`;
              const play = p.play ?? NO_RHYTHM;
              const invalid = errors.playRows[i]?.fields ?? [];
              return (
                <div key={i} className={`${styles.row} ${styles.rhythm}`}>
                  <div className={`${styles.ro} ${styles.roKey}`}>{p.key}</div>
                  <div className={`${styles.ro} ${styles.roRight}`}>{p.share}%</div>
                  {RANGES.map(({ field, unit }) => (
                    <RangeField
                      key={field}
                      label={`${who} ${PLAY_FIELD_LABELS[field]}`}
                      unit={unit}
                      min={PLAY_BOUNDS[field].lo}
                      max={PLAY_BOUNDS[field].hi}
                      value={{
                        min: play[`${field}_min`],
                        max: play[`${field}_max`],
                      }}
                      onChange={({ min, max }) =>
                        setPlay(i, {
                          ...play,
                          [`${field}_min`]: min,
                          [`${field}_max`]: max,
                        })
                      }
                      error={invalid.includes(field)}
                    />
                  ))}
                  <div className={`${styles.ro} ${styles.roRight}`}>
                    {formatOnlineWeek(onlineMinutesPerWeek(p.play))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {firstError ? (
          <div className={styles.fieldError}>{firstError}</div>
        ) : (
          <div className={styles.helper}>
            Profiles and their shares are edited in{" "}
            <a
              href="#behaviour"
              className={styles.helperLink}
              onClick={scrollTo("behaviour")}
            >
              Behaviour
            </a>
            .
          </div>
        )}
      </FieldRow>

      <GrowingStats
        growth={growth}
        onGrowth={onGrowth}
        statsSchema={statsSchema}
        errors={errors}
      />

      {growth.length > 0 && (
        <Forecast
          profiles={profiles}
          growth={growth}
          statRules={statRules}
          leaderboards={leaderboards}
          boards={boards}
        />
      )}
    </SectionCard>
  );
}

function GrowingStats({
  growth,
  onGrowth,
  statsSchema,
  errors,
}: {
  growth: BotGrowthRule[];
  onGrowth: (next: BotGrowthRule[]) => void;
  statsSchema: SchemaField[];
  errors: BotsErrors;
}) {
  const ints = statsSchema
    .filter((f) => f.type === "int" && f.key.trim() !== "")
    .map((f) => f.key);
  const unused = ints.filter((k) => !growth.some((g) => g.key === k));
  const put = (i: number, next: BotGrowthRule) =>
    onGrowth(growth.map((g, j) => (i === j ? next : g)));
  const add = () => onGrowth([...growth, defaultGrowthRule(unused[0] ?? "")]);
  const addLabel =
    ints.length === 0
      ? "+ Add stat — profile.stats_schema has no int key"
      : unused.length === 0
        ? "+ Add stat — every int stat already grows"
        : "+ Add stat";
  const firstError = growth
    .map((_, i) => errors.growthRows[i]?.message)
    .find((m) => m !== undefined);

  return (
    <FieldRow
      name="Growing stats"
      apiName="bots.growth[]"
      hint={
        <>
          A stat listed here grows on every bot, generated or from the table.{" "}
          <b className={styles.hintStrong}>During play</b> adds the gain each
          time the interval of online time passes — a level, a run, a match.{" "}
          <b className={styles.hintStrong}>Per day</b> adds it once a day
          whether or not the bot plays. A leaderboard that follows the stat
          receives every gain.
        </>
      }
      stacked
    >
      {growth.length === 0 ? (
        <div className={styles.emptyBox}>
          <span className={styles.emptyText}>
            No stat grows — bots keep the values their card gives them, and no
            leaderboard can follow a stat.
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={add}
            disabled={unused.length === 0}
          >
            {addLabel}
          </Button>
        </div>
      ) : (
        <>
          <div className={styles.gridEditor}>
            <div className={styles.scroller}>
              <div className={`${styles.row} ${styles.growth} ${styles.head}`}>
                <div>stat</div>
                <div>grows</div>
                <div className={styles.ctr}>gain</div>
                <div className={styles.ctr}>every, online</div>
                <div className={styles.num}>cap</div>
                <div />
              </div>
              {growth.map((g, i) => {
                const who = g.key.trim() || `stat ${i + 1}`;
                const bad = errors.growthRows[i]?.fields ?? [];
                // A key is offered once: the row's own, and the ones no other
                // row has taken.
                const keys = ints.filter(
                  (k) => k === g.key || !growth.some((o) => o.key === k),
                );
                return (
                  <div key={i} className={`${styles.row} ${styles.growth}`}>
                    <div
                      className={`${styles.cell} ${bad.includes("key") ? styles.selectErr : ""}`}
                    >
                      <Select
                        className={styles.rowSelect}
                        value={g.key}
                        monospaceValue
                        onChange={(key) => put(i, { ...g, key })}
                        options={[
                          ...(ints.includes(g.key) || !g.key
                            ? []
                            : [{ value: g.key, label: `${g.key} (removed)` }]),
                          ...keys.map((k) => ({ value: k, label: k, mono: true })),
                        ]}
                        placeholder="stat"
                        aria-label={`Growing stat ${i + 1}`}
                      />
                    </div>
                    <div
                      className={`${styles.cell} ${bad.includes("mode") ? styles.selectErr : ""}`}
                    >
                      <Select
                        className={styles.rowSelect}
                        value={g.mode}
                        onChange={(mode) =>
                          put(i, { ...g, mode: mode as BotGrowthMode })
                        }
                        options={MODE_OPTIONS}
                        aria-label={`${who} grows`}
                      />
                    </div>
                    <RangeField
                      label={`${who} gain`}
                      min={0}
                      value={{ min: g.gain_min, max: g.gain_max }}
                      onChange={({ min, max }) =>
                        put(i, { ...g, gain_min: min, gain_max: max })
                      }
                      error={bad.includes("gain")}
                    />
                    {g.mode === "per_day" ? (
                      <div
                        className={styles.modeFixed}
                        title="Added once a day, online or not"
                      >
                        <span>once a day</span>
                      </div>
                    ) : (
                      <RangeField
                        label={`${who} every`}
                        unit="min"
                        min={EVERY_BOUNDS.lo}
                        max={EVERY_BOUNDS.hi}
                        value={{
                          min: g.every_minutes_min,
                          max: g.every_minutes_max,
                        }}
                        onChange={({ min, max }) =>
                          put(i, {
                            ...g,
                            every_minutes_min: min,
                            every_minutes_max: max,
                          })
                        }
                        error={bad.includes("every")}
                      />
                    )}
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      placeholder="none"
                      className={`${styles.capInput} ${bad.includes("cap") ? styles.inputErr : ""}`}
                      value={g.cap === undefined ? "" : String(g.cap)}
                      onChange={(e) => {
                        const next = { ...g };
                        if (e.target.value === "") delete next.cap;
                        else next.cap = Number(e.target.value);
                        put(i, next);
                      }}
                      aria-label={`${who} cap`}
                      aria-invalid={bad.includes("cap") || undefined}
                    />
                    <button
                      type="button"
                      className={styles.rowBtn}
                      aria-label={`Remove ${who}`}
                      onClick={() => onGrowth(growth.filter((_, j) => j !== i))}
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              className={styles.addRow}
              disabled={unused.length === 0}
              onClick={add}
            >
              {addLabel}
            </button>
          </div>
          {firstError ? (
            <div className={styles.fieldError}>{firstError}</div>
          ) : (
            <div className={styles.helper}>
              Only <code>int</code> keys of <code>profile.stats_schema</code> can
              grow. A bot starts from the value its card gives it — see Card
              rules on the Source tab.
            </div>
          )}
        </>
      )}
    </FieldRow>
  );
}

function Forecast({
  profiles,
  growth,
  statRules,
  leaderboards,
  boards,
}: {
  profiles: BotProfile[];
  growth: BotGrowthRule[];
  statRules: BotProfileStatRule[];
  leaderboards: BotsLeaderboardSim[];
  boards: BoardShape[];
}) {
  const [period, setPeriod] = useState<ForecastPeriod>("week");
  const stats = growth.filter((g) => g.key.trim() !== "");
  const starts = stats.map((g) => startMean(statRules, g.key));
  const cells = profiles.map((p) =>
    stats.map((g, c) => {
      const start = starts[c];
      const perWeek = gainPerWeek(g, p.play);
      const weeks = start === null ? null : weeksToCap(g.cap, start, perWeek);
      return { perWeek, weeks };
    }),
  );
  // Only a switched-on board whose operator lets it follow a stat ever does.
  const followers = (key: string) =>
    leaderboards.filter(
      (l) =>
        l.enabled &&
        l.score_stat === key &&
        scoreModeCell(
          boards.find((b) => b.ext_key === l.ext_key),
          true,
        ).kind === "select",
    );
  // A column that has to fit a «cap in ~N wk» label is the mockup's wide one.
  const widths = stats.map((_, c) =>
    cells.some((row) => row[c].weeks !== null) ? 210 : 150,
  );
  const grid = {
    gridTemplateColumns: ["minmax(110px, 1fr)", ...widths.map((w) => `${w}px`)].join(" "),
    minWidth: 24 + 110 + widths.reduce((a, w) => a + w + 12, 0),
  };

  return (
    <FieldRow
      name="Forecast"
      apiName="read-only"
      aside={
        <SegmentedToggle
          aria-label="Forecast period"
          value={period}
          onChange={setPeriod}
          options={PERIOD_OPTIONS}
        />
      }
      hint={`What one bot of each profile gains in an average ${period} with the numbers above. Sessions are random, so single bots spread around it.`}
      stacked
    >
      <div className={styles.gridEditor}>
        <div className={styles.scroller}>
          <div className={`${styles.row} ${styles.forecast} ${styles.head}`} style={grid}>
            <div>profile</div>
            {stats.map((g, c) => (
              <div key={c} className={`${styles.num} ${styles.mono}`}>
                {g.key}
              </div>
            ))}
          </div>
          {profiles.map((p, r) => (
            <div key={r} className={`${styles.row} ${styles.forecast}`} style={grid}>
              <div className={`${styles.ro} ${styles.roKey}`}>{p.key}</div>
              {stats.map((g, c) => {
                const { perWeek, weeks } = cells[r][c];
                if (starts[c] === null) {
                  return (
                    <div
                      key={c}
                      className={`${styles.ro} ${styles.roRight} ${styles.fcNone}`}
                      title={`Generated bots don't carry ${g.key} — give it a start in Card rules`}
                    >
                      —
                    </div>
                  );
                }
                return (
                  <div
                    key={c}
                    className={`${styles.ro} ${styles.roRight} ${styles.roStrong}`}
                  >
                    {weeks !== null && (
                      <span className={styles.fcCap}>{capLabel(weeks)}</span>
                    )}
                    {perWeek === null ? "—" : formatGain(gainPerPeriod(perWeek, period))}
                  </div>
                );
              })}
            </div>
          ))}
          <div className={`${styles.row} ${styles.forecast} ${styles.fcBoards}`} style={grid}>
            <div className={styles.ro}>followed by</div>
            {stats.map((g, c) => {
              const following = followers(g.key);
              return following.length === 0 ? (
                <div key={c} className={`${styles.ro} ${styles.roRight} ${styles.fcNone}`}>
                  no board
                </div>
              ) : (
                <div key={c} className={`${styles.ro} ${styles.roRight}`}>
                  {following.map((l, k) => (
                    <Fragment key={k}>
                      {k > 0 && ", "}
                      <a href="#leaderboards" onClick={scrollTo("leaderboards")}>
                        {l.ext_key}
                      </a>
                    </Fragment>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </FieldRow>
  );
}
